import { $isHeadingNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { List } from 'immutable';
import { $getSelection, $isRangeSelection, $selectAll, $getRoot, ElementNode, TextNode, LexicalNode, $isBlockElementNode } from 'lexical';
import { getTextStylesFromBrandStyle } from 'modules/BrandDefinition';
import { $createCustomHeadingNode, $isCustomHeadingNode } from 'modules/Lexical/nodes/CustomHeadingNode';
import { $createCustomParagraphNode, CustomParagraphNode } from 'modules/Lexical/nodes/CustomParagraphNode';
import { HEADING_TAGS, HeadingTagType } from 'const';
import { BrandStyleMap } from 'models';

export function isValidHeadingTag(tag: unknown): tag is HeadingTagType {

  if (typeof tag !== 'string') {
    return false;
  }

  const validTags: readonly string[] = HEADING_TAGS;

  return validTags.includes(tag);
}

export function $getHeadingTag(): HeadingTagType | undefined {
  const selection = $getSelection();
  let currentHeadingTag: HeadingTagType | undefined = undefined;
  if ($isRangeSelection(selection)) {
    const anchorNode = selection.anchor.getNode();
    const topLevelElement = anchorNode.getTopLevelElement();

    if ($isHeadingNode(topLevelElement) || $isCustomHeadingNode(topLevelElement)) {
      currentHeadingTag = topLevelElement.getTag();
    }
  }

  return currentHeadingTag;
}

export function $applyBrandHeadingTag(brandStyle?: BrandStyleMap): void {
  if (!brandStyle) {
    return;
  }
  const rangeSelection = $selectAll();
  const headingTag = brandStyle.get('tag');
  const textStyles = getTextStylesFromBrandStyle(brandStyle, List([]), List([]));
  const { lineHeight, fontSize } = textStyles;
  if (isValidHeadingTag(headingTag)) {
    $setBlocksType(rangeSelection, () => $createCustomHeadingNode(headingTag, lineHeight, fontSize));

    return;
  }
  const hasHeadingTagAlready = $getRoot().getChildren().some($isCustomHeadingNode);
  if (!hasHeadingTagAlready) {
    return;
  }

  $setBlocksType(rangeSelection, () => $createCustomParagraphNode());
}

type ReplaceProps = {
  headingTag: HeadingTagType;
  lineHeight: number;
  fontSize: number;
  paragraphs: Set<CustomParagraphNode>;
};

export function $replaceParagraphsWithHeadingTags({ headingTag, lineHeight, fontSize, paragraphs }: ReplaceProps): void {
  paragraphs.forEach((paragraph) => {
    const paragraphChildren = paragraph.getChildren();
    const headingNode = $createCustomHeadingNode(headingTag, lineHeight, fontSize);
    headingNode.append(...paragraphChildren);
    paragraph.replace(headingNode);
  });
}

export function $getPrevNonEmptyNode(node: ElementNode | TextNode): LexicalNode | null {
  let prevNode = node.getPreviousSibling();
  while (prevNode?.getTextContent().trim().length === 0) {
    prevNode = prevNode.getPreviousSibling();
    if (prevNode === null) {
      break;
    }
  }
  if ($isBlockElementNode(prevNode)) {
    return prevNode.getLastChild();
  }

  return prevNode;
}
